import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  inject,
  computed,
  OnDestroy,
  input,
} from '@angular/core';
import { AvatarsComponent, HighlightDirective } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TypedChanges } from '@libs/utils';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { destructureFullSeries, FullSeriesPreviewComponent, FullSeriesVM, IMAGING_ATTACHMENT } from '@libs/imaging';
import { Dialog } from '@angular/cdk/dialog';
import { AttachmentComponent } from '../attachment';
import { CollaborationFullSeriesPreviewDialogComponent } from '../collaboration-full-series-preview-dialog';
import { CheckboxComponent } from '../../../../../ui-lib/src/lib/components/checkbox/checkbox.component';
import {
  CollaborationFullSeriesPreviewDialogResult,
  CollaborationFullSeriesPreviewDialogVM,
} from '../collaboration-full-series-preview-dialog/collaboration-full-series-preview-dialog.interface';
import { DownloadAttachmentsService } from '../../services';
import { MessageVM } from './message.vm';

@Component({
  selector: 'lib-message',
  templateUrl: 'message.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: IMAGING_ATTACHMENT,
      useValue: true,
    },
  ],
  imports: [
    CommonModule,
    AttachmentComponent,
    AvatarsComponent,
    HighlightDirective,
    SvgIconComponent,
    TranslateModule,
    MatMenuModule,
    LottieComponent,
    FullSeriesPreviewComponent,
    CheckboxComponent,
    PushTagOnClickDirective,
  ],
})
export class MessageComponent implements OnChanges, OnDestroy {
  private readonly dialog = inject(Dialog);
  private readonly downloadAttachmentsService = inject(DownloadAttachmentsService);

  @Input() public message: MessageVM | undefined;
  @Input() public searchTextToHighlight: string | null | undefined;
  @Input() public showcaseMode = false;
  public isSupportCollab = input(false);
  public participantCount = input<number>(0);
  public isCollabSoftDeleted = input<boolean>(false);

  @Output() public unsendMessage = new EventEmitter<void>();
  @Output() public markMessageUnread = new EventEmitter<void>();
  @Output() public downloadTap = new EventEmitter<void>();
  @Output() public downloadAttachments = new EventEmitter<void>();
  @Output() public selection = new EventEmitter<'single' | 'multiple' | 'all'>();
  @Output() public deselection = new EventEmitter<'single' | 'multiple' | 'all'>();
  @Output() public retrySendMessage = new EventEmitter<string>();

  protected selectingAttachments = computed(
    () => this.downloadAttachmentsService.viewModel().messageInDownloadMode === (this.message?.id ?? ''),
  );
  protected selectedAttachments = computed(() => this.downloadAttachmentsService.viewModel().selectedAttachments);
  protected selectedAttachmentIds = computed(() => this.downloadAttachmentsService.viewModel().selectedAttachmentIds);
  protected readonly saveToPmsProgress = computed(() => this.downloadAttachmentsService.viewModel().saveToPmsProgress);
  protected readonly saveToPmsMode = computed(() => this.downloadAttachmentsService.viewModel().saveToPmsMode);
  protected readonly saveToDesktopMode = computed(() => this.downloadAttachmentsService.viewModel().saveToDesktopMode);
  protected readonly saveToPmsSuccess = computed(() => this.downloadAttachmentsService.viewModel().savetoPmsSuccess);
  protected readonly destructuredFullSeries = computed(() => {
    if (this.message?.attachments?.fullSeries) {
      return destructureFullSeries(this.message?.attachments?.fullSeries);
    }
    return null;
  });
  protected readonly selectedFullSeriesItems = computed(() => {
    return this.selectedAttachments()
      .filter((selectedImage) => this.destructuredFullSeries()?.some((image) => image.id === selectedImage.id))
      .map((selectedImage) => selectedImage.id);
  });
  protected readonly selectedFullSeriesCount = computed(() => this.selectedFullSeriesItems().length);
  protected readonly isFullSeriesFullySelected = computed(() => {
    return this.destructuredFullSeries()?.every((image) => this.selectedAttachments().find((attachment) => attachment.id === image.id));
  });
  protected readonly isFullSeriesPartiallySelected = computed(() => {
    if (this.destructuredFullSeries()) {
      return this.destructuredFullSeries()?.some((image) => this.selectedAttachments().find((attachment) => attachment.id === image.id));
    }
    return false;
  });

  @HostBinding('style.gridTemplateColumns') public get gridTemplateColumns(): string {
    return this.message?.sender.isSelf ? '75% max-content ' : 'max-content 75%';
  }

  @HostBinding('class.justify-end') public get justifySelf(): boolean {
    return !!this.message?.sender.isSelf;
  }

  protected isWithin7days = false;
  protected TagEvent = TagEvent;
  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public ngOnChanges(changes: TypedChanges<MessageComponent>): void {
    if (changes.message) {
      this.isWithin7days = this.isSentWithin7Days(changes.message.currentValue?.sentAt ?? '');
    }
  }

  public ngOnDestroy(): void {
    this.downloadAttachmentsService.resetMessageInDownloadMode();
    this.downloadAttachmentsService.setAttachments([]);
  }

  public isSentWithin7Days(sentAt: string): boolean {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const SEVEN_DAYS_IN_HOURS = 7 * 24;
    const messageDate = new Date(sentAt);
    const currentDate = new Date();
    const differenceInHours = (currentDate.getTime() - messageDate.getTime()) / 1000 / 60 / 60;
    return differenceInHours <= SEVEN_DAYS_IN_HOURS;
  }

  protected toggleFullSeriesSelection(event: MouseEvent, selectAll: boolean): void {
    event.preventDefault();
    event.stopPropagation();
    const destructuredFullSeries = this.destructuredFullSeries();
    if (destructuredFullSeries && selectAll) {
      const allImages = destructuredFullSeries.map((image) => image.id);
      this.toggleMultipleAttachmentSelection(allImages, []);
    } else {
      this.toggleMultipleAttachmentSelection([], destructuredFullSeries?.map((image) => image.id) ?? []);
    }
  }

  protected onSaveToPmsModeClick(): void {
    this.downloadAttachmentsService.setSaveToPmsMode(true);
    this.downloadAttachmentsService.setAttachments(this.message?.attachments?.allAttachments ?? []);
    this.downloadTap.emit();
  }

  protected onSaveToDesktopModeClick(): void {
    this.downloadAttachmentsService.setSaveToDesktopMode(true);
    this.downloadAttachmentsService.setAttachments(this.message?.attachments?.allAttachments ?? []);
    this.downloadTap.emit();
  }

  protected setToDualMode(): void {
    this.downloadAttachmentsService.setSaveToDesktopMode(true);
    this.downloadAttachmentsService.setSaveToPmsMode(true);
    this.downloadTap.emit();
  }

  protected onSaveToPmsClick(): void {
    this.downloadAttachmentsService.saveToPMS();
  }

  protected onDeselectAttachmentsClick(): void {
    this.downloadAttachmentsService.resetMessageInDownloadMode();
    this.downloadAttachmentsService.setAttachments([]);
    this.deselection.emit('all');
  }

  protected toggleAttachmentSelection(attachmentId: string): void {
    const attachment = this.message?.attachments?.allAttachments.find((attachment) => attachment.id === attachmentId);

    if (this.selectedAttachments().some((attachment) => attachment.id === attachmentId)) {
      this.downloadAttachmentsService.setAttachments(this.selectedAttachments().filter((attachment) => attachment.id !== attachmentId));
      this.deselection.emit(this.selectedAttachments().length > 1 ? 'multiple' : this.selectedAttachments().length > 0 ? 'single' : 'all');

      return;
    }

    if (attachment) {
      this.downloadAttachmentsService.setAttachments([...this.selectedAttachments(), attachment]);
    }

    const selectedLength = this.selectedAttachments().length;
    const attachmentsLength = this.message?.attachments?.allAttachments.length ?? 0;
    if (selectedLength === attachmentsLength) {
      this.selection.emit('all');
    } else if (selectedLength > 1) {
      this.selection.emit('multiple');
    } else if (selectedLength === 1) {
      this.selection.emit('single');
    }
  }

  protected cancelSaveToPms(): void {
    this.downloadAttachmentsService.cancelSavetoPms();
  }

  protected toggleMultipleAttachmentSelection(attachmentIds: string[], deselectedIds: string[]): void {
    const attachments = this.message?.attachments?.allAttachments.filter((attachment) => attachmentIds.includes(attachment.id)) ?? [];
    if (deselectedIds.length > 0) {
      this.downloadAttachmentsService.setAttachments([
        ...this.selectedAttachments().filter((attachment) => !deselectedIds.includes(attachment.id)),
        ...attachments,
      ]);
      this.deselection.emit(this.selectedAttachments().length > 1 ? 'multiple' : this.selectedAttachments().length > 0 ? 'single' : 'all');
    } else {
      this.downloadAttachmentsService.setAttachments([...this.selectedAttachments(), ...attachments]);
      const selectedLength = this.selectedAttachments().length;
      const attachmentsLength = this.message?.attachments?.allAttachments.length ?? 0;
      if (selectedLength === attachmentsLength) {
        this.selection.emit('all');
      } else if (selectedLength > 1) {
        this.selection.emit('multiple');
      } else if (selectedLength === 1) {
        this.selection.emit('single');
      }
    }
  }

  protected onDownloadAttachmentsClick(): void {
    this.downloadAttachments.emit();
    this.downloadAttachmentsService.resetMessageInDownloadMode();
    this.downloadAttachmentsService.setAttachments([]);
  }

  protected onRetryClick(): void {
    if (!this.message) return;
    this.retrySendMessage.emit(this.message.id);
  }

  protected onPreviewFullSeriesClick(event: MouseEvent, fullSeries: FullSeriesVM): void {
    event.preventDefault();
    this.dialog
      .open<CollaborationFullSeriesPreviewDialogResult, CollaborationFullSeriesPreviewDialogVM>(
        CollaborationFullSeriesPreviewDialogComponent,
        {
          data: {
            fullSeries,
            downloadMode: !!this.selectingAttachments(),
            selectedImages: this.selectedAttachments(),
            selectedImageIds: this.selectedAttachmentIds(),
            previewMode: true,
          } as CollaborationFullSeriesPreviewDialogVM,
          providers: [{ provide: IMAGING_ATTACHMENT, useValue: true }],
        },
      )
      .closed.subscribe((result) => {
        if (result?.setDownloadMode && !this.selectingAttachments()) {
          this.setToDualMode();
        }
        if (result?.selectedImages) {
          this.toggleMultipleAttachmentSelection(result.selectedImages, result.deselectedImages ?? []);
        }
      });
  }

  // Edit message will be implemented later
  protected onEditMessageClick(): void {}

  protected onDeleteMessageClick(): void {
    this.unsendMessage.emit();
  }

  // Reply message will be implemented later
  protected onReplyMessageClick(): void {}

  // Mark message as unread will be implemented later
  protected onMarkMessageUnreadClick(): void {
    this.markMessageUnread.emit();
  }
}
